import type {
  AIModelService,
  ReasoningModeId,
} from '@affine/core/modules/ai-button/services/models';
import {
  type AIReasoningService,
  reasoningModeOfChoice,
} from '@affine/core/modules/ai-button/services/reasoning';
import {
  type AuthAccountInfo,
  type AuthService,
  type AvatarAssetCacheInput,
  type AvatarAssetCacheService,
  type ServerService,
  toAbsoluteAvatarAssetUrl,
} from '@affine/core/modules/cloud';
import type { GlobalDialogService } from '@affine/core/modules/dialogs';
import {
  type AddContextFileInput,
  ContextCategories,
  type ContextWorkspaceEmbeddingStatus,
  type getCopilotHistoriesQuery,
  type PaginationInput,
  type QueryChatSessionsInput,
  type RequestOptions,
  type UpdateChatSessionInput,
} from '@affine/graphql';

import { transformHistoryAttachments } from '../utils/blob-url-resolver';
import { getAIChatOnlineStatus } from '../utils/chat-connectivity';
import {
  ALL_TEXT_ACTIONS,
  buildTextActionContent,
  CHAT_ACTION,
} from './ai-actions';
import { AIProvider } from './ai-provider';
import type { CopilotClient } from './copilot-client';
import { GeneralNetworkError } from './error';
import type { PromptKey } from './prompt';
import { attachToChatTurn, textToText, toImage } from './request';
import { setupTracker } from './tracker';

function toAIUserInfo(account: AuthAccountInfo | null) {
  if (!account) return null;
  return {
    avatarUrl: account.avatar ?? '',
    email: account.email ?? '',
    id: account.id,
    name: account.label,
  };
}

async function toCachedAIUserInfo(
  account: AuthAccountInfo | null,
  avatarCache?: AvatarAssetCacheService,
  serverService?: ServerService
) {
  const userInfo = toAIUserInfo(account);
  if (!account || !userInfo) return null;
  let avatarUrl = account.avatar ?? '';
  const sourceUrl =
    account.avatar && serverService
      ? toAbsoluteAvatarAssetUrl(account.avatar, serverService.server.baseUrl)
      : null;

  if (avatarCache && serverService && sourceUrl) {
    const input: AvatarAssetCacheInput = {
      kind: 'user-avatar',
      scope: {
        accountId: account.id,
        serverId: serverService.server.id,
      },
      entityId: account.id,
      source: sourceUrl,
    };
    avatarUrl =
      (await avatarCache
        .objectUrl(input, serverService.server.baseUrl)
        .catch(() => null)) ??
      account.avatar ??
      '';
  }

  return {
    ...userInfo,
    avatarUrl,
  };
}

const processTypeToPromptName = new Map<string, PromptKey>(
  Object.entries({
    Clearer: 'Upscale image',
    'Remove background': 'Remove background',
    'Convert to sticker': 'Convert to sticker',
  })
);

export function setupAIProvider(
  client: CopilotClient,
  globalDialogService: GlobalDialogService,
  authService: AuthService,
  workspaceEngine: {
    embeddingWorkspaceProgress$:
      | import('rxjs').Observable<{
          docsIndexed: number;
          docsTotal: number;
        }>
      | null;
    embeddingDocStatus$:
      | import('rxjs').Observable<{
          docId: string;
          status: string;
        }>
      | null;
  },
  aiModelService?: AIModelService,
  avatarCache?: AvatarAssetCacheService,
  serverService?: ServerService,
  reasoningService?: AIReasoningService
) {
  const assertAIChatOnline = () => {
    if (!getAIChatOnlineStatus()) {
      throw new GeneralNetworkError(
        'NETWORK_ERROR: The Mate is not available without a server connection.'
      );
    }
  };

  const resolveModelId = async (options: {
    modelId?: string;
    workspaceId?: string;
  }) => {
    if (options.modelId) return options.modelId;
    if (!aiModelService || !options.workspaceId) return undefined;

    const modelId = await aiModelService.resolveChatModelIdForWorkspace(
      options.workspaceId
    );
    if (!modelId) {
      throw new GeneralNetworkError(
        'AI_MODEL_UNAVAILABLE: The Mate is not available in this workspace because no chat model is configured.'
      );
    }
    return modelId;
  };

  async function createSession({
    promptName,
    workspaceId,
    docId,
    sessionId,
    retry,
    pinned,
    reuseLatestChat,
    model,
    reasoningMode,
  }: BlockSuitePresets.AICreateSessionOptions & {
    model?: string;
    reasoningMode?: ReasoningModeId;
  }) {
    if (sessionId) return sessionId;
    if (retry) return AIProvider.LAST_ACTION_SESSIONID;

    return client.createSession({
      workspaceId,
      docId,
      promptName,
      pinned,
      reuseLatestChat,
      model,
      reasoningMode,
    });
  }

  /** A new agent starts with the last chosen model and thinking depth. */
  function createAgentSession(
    options: BlockSuitePresets.AICreateSessionOptions
  ) {
    return createSession({
      ...options,
      model: aiModelService?.modelId.value,
      reasoningMode:
        reasoningService &&
        reasoningModeOfChoice(
          reasoningService.enabled.value,
          reasoningService.effort.value
        ),
    });
  }

  AIProvider.provide('userInfo', () => {
    return toAIUserInfo(authService.session.account$.value);
  });

  let accountGeneration = 0;
  const accountSubscription = authService.session.account$.subscribe(
    account => {
      const generation = ++accountGeneration;
      void toCachedAIUserInfo(account, avatarCache, serverService).then(
        userInfo => {
          if (generation === accountGeneration) {
            AIProvider.slots.userInfo.next(userInfo);
          }
        }
      );
    }
  );

  //#region actions

  // Chat action - special handling for webSearch
  AIProvider.provide('chat', async options => {
    const { input } = options;
    assertAIChatOnline();
    const modelId = await resolveModelId(options);

    const sessionId = await createSession({
      promptName: CHAT_ACTION.prompt,
      ...options,
    });
    return textToText({
      ...options,
      modelId,
      client,
      sessionId,
      threadId: sessionId, // threadId = sessionId, always!
      content: input,
      timeout: CHAT_ACTION.timeout,
      endpoint: CHAT_ACTION.endpoint,
      // sl-ddz8.5.3: Pass currently selected bot to backend
      mateId: AIProvider.currentMateId ?? undefined,
    });
  });

  // Data-driven text actions - one loop instead of 20+ blocks
  for (const [actionName, config] of Object.entries(ALL_TEXT_ACTIONS)) {
    // Type assertion needed because AIProvider.provide expects specific action keys
    (AIProvider.provide as any)(actionName, async (options: any) => {
      assertAIChatOnline();
      // expandMindmap requires input validation
      if (actionName === 'expandMindmap' && !options.input) {
        throw new Error('expandMindmap action requires input');
      }

      const content = buildTextActionContent(config, options);
      const modelId = await resolveModelId(options);
      const sessionId = await createSession({
        ...options,
        promptName: config.prompt,
        reuseLatestChat: false,
      });

      return textToText({
        ...options,
        client,
        sessionId,
        threadId: sessionId, // threadId = sessionId, always!
        content,
        // Text transformations propose an editor result; they never execute workspace tools.
        enableTools: false,
        ...(config.timeout ? { timeout: config.timeout } : {}),
        ...(config.endpoint ? { endpoint: config.endpoint } : {}),
        modelId,
      });
    });
  }

  AIProvider.provide('makeItReal', async options => {
    assertAIChatOnline();
    let promptName: PromptKey = 'Make it real';
    let content = options.input || '';

    // wireframes
    if (options.attachments?.length) {
      content = `Here are the latest wireframes. Could you make a new website based on these wireframes and notes and send back just the html file?
Here are our design notes:\n ${content}.`;
    } else {
      // notes
      promptName = 'Make it real with text';
      content = `Here are the latest notes: \n ${content}.
Could you make a new website based on these notes and send back just the html file?`;
    }

    const sessionId = await createSession({
      promptName,
      ...options,
    });
    const modelId = await resolveModelId(options);

    return textToText({
      ...options,
      client,
      sessionId,
      threadId: sessionId,
      content,
      modelId,
    });
  });

  // Image actions - special handling with toImage
  AIProvider.provide('createImage', async options => {
    assertAIChatOnline();
    const sessionId = await createSession({
      promptName: 'Generate image',
      ...options,
    });
    return toImage({
      ...options,
      client,
      sessionId,
      threadId: sessionId,
      content:
        !options.input && options.attachments
          ? 'Make the image more detailed.'
          : options.input,
      timeout: 300000,
    });
  });

  AIProvider.provide('processImage', async options => {
    assertAIChatOnline();
    const promptName: PromptKey | undefined = processTypeToPromptName.get(
      options.type
    );
    if (!promptName) {
      throw new Error('processImage requires a promptName');
    }
    const sessionId = await createSession({
      promptName,
      ...options,
    });
    return toImage({
      ...options,
      client,
      sessionId,
      threadId: sessionId,
      content: options.input,
      timeout: 180000,
    });
  });

  // NOTE: generateCaption and continueWriting are handled by ALL_TEXT_ACTIONS loop
  //#endregion

  AIProvider.provide('session', {
    createSession: createAgentSession,
    getSession: async (workspaceId: string, sessionId: string) => {
      return client.getSession(workspaceId, sessionId);
    },
    getSessions: async (
      workspaceId: string,
      docId?: string,
      options?: QueryChatSessionsInput,
      pagination: PaginationInput = {}
    ) => {
      return client.getSessions(workspaceId, pagination, docId, options);
    },
    getRecentSessions: async (
      workspaceId: string,
      limit?: number,
      offset?: number
    ) => {
      return client.getRecentSessions(workspaceId, limit, offset);
    },
    updateSession: async (options: UpdateChatSessionInput) => {
      return client.updateSession(options);
    },
    attachRunningTurn: (workspaceId, sessionId, { stop, close }) =>
      attachToChatTurn({ client, workspaceId, sessionId, stop, close }),
    chatQueue: (workspaceId, sessionId) =>
      client.chatQueue(sessionId, workspaceId),
    removeQueuedMessage: (workspaceId, sessionId, messageId) =>
      client.removeQueuedChatMessage(sessionId, workspaceId, messageId),
    steerQueuedMessage: (workspaceId, sessionId, messageId) =>
      client.steerQueuedChatMessage(sessionId, workspaceId, messageId),
  });

  AIProvider.provide('context', {
    createContext: async (workspaceId: string, sessionId: string) => {
      return client.createContext(workspaceId, sessionId);
    },
    getContextId: async (workspaceId: string, sessionId: string) => {
      return client.getContextId(workspaceId, sessionId);
    },
    addContextDoc: async (options: {
      workspaceId: string;
      contextId: string;
      docId: string;
    }) => {
      return client.addContextDoc(options);
    },
    removeContextDoc: async (options: {
      workspaceId: string;
      contextId: string;
      docId: string;
    }) => {
      return client.removeContextDoc(options);
    },
    addContextFile: async (file: File, options: AddContextFileInput) => {
      return client.addContextFile(file, options);
    },
    removeContextFile: async (options: {
      workspaceId: string;
      contextId: string;
      fileId: string;
    }) => {
      return client.removeContextFile(options);
    },
    addContextTag: async (options: {
      workspaceId: string;
      contextId: string;
      tagId: string;
      docIds: string[];
    }) => {
      return client.addContextCategory({
        workspaceId: options.workspaceId,
        contextId: options.contextId,
        type: ContextCategories.Tag,
        categoryId: options.tagId,
        docs: options.docIds,
      });
    },
    removeContextTag: async (options: {
      workspaceId: string;
      contextId: string;
      tagId: string;
    }) => {
      return client.removeContextCategory({
        workspaceId: options.workspaceId,
        contextId: options.contextId,
        type: ContextCategories.Tag,
        categoryId: options.tagId,
      });
    },
    addContextCollection: async (options: {
      workspaceId: string;
      contextId: string;
      collectionId: string;
      docIds: string[];
    }) => {
      return client.addContextCategory({
        workspaceId: options.workspaceId,
        contextId: options.contextId,
        type: ContextCategories.Collection,
        categoryId: options.collectionId,
        docs: options.docIds,
      });
    },
    removeContextCollection: async (options: {
      workspaceId: string;
      contextId: string;
      collectionId: string;
    }) => {
      return client.removeContextCategory({
        workspaceId: options.workspaceId,
        contextId: options.contextId,
        type: ContextCategories.Collection,
        categoryId: options.collectionId,
      });
    },
    getContextDocsAndFiles: async (
      workspaceId: string,
      sessionId: string,
      contextId: string
    ) => {
      return client.getContextDocsAndFiles(workspaceId, sessionId, contextId);
    },
    // sl-00ro: Event-driven embedding status via nbstore WebSocket (replaces 10s GraphQL polling)
    pollEmbeddingStatus: async (
      workspaceId: string,
      onPoll: (result: ContextWorkspaceEmbeddingStatus) => void,
      abortSignal: AbortSignal
    ) => {
      // Initial fetch for current state
      const initialResult = await client.getEmbeddingStatus(workspaceId);
      if (abortSignal.aborted) return;
      onPoll(initialResult);

      // Subscribe to nbstore embedding events for live updates
      const subscriptions: Array<{ unsubscribe: () => void }> = [];
      let debounceTimer: ReturnType<typeof setTimeout> | null = null;

      const debouncedRefetch = () => {
        if (abortSignal.aborted) return;
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          if (abortSignal.aborted) return;
          client
            .getEmbeddingStatus(workspaceId)
            .then(result => {
              if (!abortSignal.aborted) onPoll(result);
            })
            .catch(() => {
              // Silently ignore fetch errors — next event will retry
            });
        }, 500);
      };

      const progress$ = workspaceEngine.embeddingWorkspaceProgress$;
      if (progress$) {
        subscriptions.push(progress$.subscribe(debouncedRefetch));
      }

      const docStatus$ = workspaceEngine.embeddingDocStatus$;
      if (docStatus$) {
        subscriptions.push(docStatus$.subscribe(debouncedRefetch));
      }

      // Keep alive until aborted (matches original contract)
      await new Promise<void>(resolve => {
        if (abortSignal.aborted) {
          resolve();
          return;
        }
        abortSignal.addEventListener('abort', () => {
          for (const sub of subscriptions) sub.unsubscribe();
          if (debounceTimer) clearTimeout(debounceTimer);
          resolve();
        });
      });
    },
    matchContext: async (
      content: string,
      contextId?: string,
      workspaceId?: string,
      limit?: number,
      scopedThreshold?: number,
      threshold?: number
    ) => {
      return client.matchContext(
        content,
        contextId,
        workspaceId,
        limit,
        scopedThreshold,
        threshold
      );
    },
    addContextBlob: async (options: {
      workspaceId: string;
      blobId: string;
      contextId: string;
    }) => {
      return client.addContextBlob({
        workspaceId: options.workspaceId,
        contextId: options.contextId,
        blobId: options.blobId,
      });
    },
    removeContextBlob: async (options: {
      workspaceId: string;
      blobId: string;
      contextId: string;
    }) => {
      return client.removeContextBlob({
        workspaceId: options.workspaceId,
        contextId: options.contextId,
        blobId: options.blobId,
      });
    },
  });

  AIProvider.provide('histories', {
    actions: async (
      workspaceId: string,
      docId: string
    ): Promise<BlockSuitePresets.AIHistory[]> => {
      // @ts-expect-error - 'action' is missing in server impl
      return (
        (await client.getHistories(workspaceId, {}, docId, {
          action: true,
          withPrompt: true,
          withMessages: true,
        })) ?? []
      );
    },
    chats: async (
      workspaceId: string,
      sessionId: string,
      docId?: string
    ): Promise<BlockSuitePresets.AIHistory[]> => {
      const histories =
        (await client.getHistories(workspaceId, {}, docId, {
          sessionId,
          withMessages: true,
        })) ?? [];

      // Transform blob IDs to loadable URLs for image attachments
      // Type assertion needed because transformHistoryAttachments preserves structure
      return histories.map(history =>
        transformHistoryAttachments(history, workspaceId)
      ) as BlockSuitePresets.AIHistory[];
    },
    cleanup: async (
      workspaceId: string,
      docId: string | undefined,
      sessionIds: string[]
    ) => {
      await client.cleanupSessions({ workspaceId, docId, sessionIds });
    },
    ids: async (
      workspaceId: string,
      docId?: string,
      options?: RequestOptions<
        typeof getCopilotHistoriesQuery
      >['variables']['options']
    ): Promise<BlockSuitePresets.AIHistoryIds[]> => {
      // @ts-expect-error - 'action' is missing in server impl
      return await client.getHistoryIds(workspaceId, {}, docId, options);
    },
  });

  AIProvider.provide('forkChat', options => {
    return client.forkSession(options);
  });

  const disposeRequestLoginHandler = AIProvider.slots.requestLogin.subscribe(
    () => {
      globalDialogService.open('sign-in', {});
    }
  );

  setupTracker();

  return () => {
    disposeRequestLoginHandler.unsubscribe();
    accountSubscription.unsubscribe();
  };
}
